import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PageLoader from "../components/UI/PageLoader";
import GeneratePDF from "../components/UI/GeneratePDF";
import ReportNotFound from "./ReportNotFound";

import ReportAMR from "./ReportAMR";
import ReportRMR from "./ReportRMR";

import classes from "./Report.module.css";
import CustomReportRMR from "./CustomReportRMR";
import CustomReportAMR from "./CustomReportAMR";
import html2canvas from "html2canvas";
import ReportRMRCheatsheet from "./ReportRMRCheatsheet";
import {
  getReportDataPlatform,
  getReportDataUser,
  GetNewReport,
  GetNewReportDataPlatform,
} from "../../../common/network/Api";
import { rememberRedirectUrl } from "../../../common/utils/redirectUrl";
import { ReportDataType } from "./reportDataTypes";
import { NewReportData } from "../../new-reports/types/newReportTypes";
import { useAppStore } from "../../../providers/AppStore";
import { getAyurmaStrings } from "../i18n/ayurma";
import { ReportLang, resolveReportLang } from "../i18n/reportLang";
import ReportLangPicker from "../components/UI/ReportLangPicker";
import { getWhiteLabelTheme } from "../whiteLabel/themes";

export type SvgConverterType = (
  content: string,
  result: string,
  imgWidth: number
) => Promise<void>;

const Report = () => {
  const [data, setData] = useState<ReportDataType>();
  const [newData, setNewData] = useState<NewReportData>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isCreatingPDF, setIsCreatingPDF] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  let id = searchParams.get("id");
  let type = searchParams.get("type");
  let lang = searchParams.get("lang");
  let isNativeApp = searchParams.get("isNativeApp");
  let isPlatformApp = searchParams.get("P");
  // Language for frontend-owned report copy (white-label sections the backend
  // does not localize). `lang` keeps going to the API verbatim; `uiLang` is an
  // optional override that re-renders without refetching, which is how the
  // interim mixed-language state (localized frontend text over English backend
  // text) can be reviewed while the backend is still English-only.
  const uiLang = resolveReportLang(searchParams.get("uiLang") ?? lang);

  /**
   * Writes the picked language to `lang`, the canonical param — so the URL stays
   * shareable and the backend gets the same language the reader is seeing, once
   * it serves more than English.
   *
   * `uiLang` is dropped because it overrides `lang` above; leaving a stale one in
   * place would silently ignore the click. Replacing rather than pushing keeps the
   * URL current without stacking a history entry per click, which would otherwise
   * trap the reader inside the report when they press Back.
   *
   * The re-render is immediate: `uiLang` is derived from the params, so every
   * frontend-owned string switches on the click. Changing `lang` also refetches
   * (it is in the effect's deps), which refreshes the backend-owned text
   * underneath without clearing `data`, so the page never blanks.
   */
  const handleLangChange = (next: ReportLang) => {
    const params = new URLSearchParams(searchParams);
    params.set("lang", next);
    params.delete("uiLang");
    setSearchParams(params, { replace: true });
  };
  const location = useLocation();
  const { ASGetLogicCategoriesList, ASGetBiomarkersList } = useAppStore();

  const getReportDataForPlatformUser = async (
    id: string,
    type: string,
    lang: string
  ) => {
    try {
      const response: ReportDataType = await getReportDataPlatform(
        id,
        type,
        lang
      );
      // // TODO: Remove - Mock customType for testing
      // response.customType = "diPietroHealth";
      const newReportData = await GetNewReportDataPlatform(response.CUSTOMERID);
      // Batch all state updates together to trigger a single render
      setData(response);
      setNewData(newReportData);
      setIsLoading(false);
    } catch (e: any) {
      console.log(e);
      setIsLoading(false);
    }
  };

  const getReportDataForUser = async (
    id: string,
    type: string,
    lang: string
  ) => {
    try {
      const response: ReportDataType = await getReportDataUser(id, type, lang);
      const newReportData = await GetNewReport(response.CUSTOMERID);
      // Batch all state updates together to trigger a single render
      setData(response);
      setNewData(newReportData);
      setIsLoading(false);
    } catch (e: any) {
      console.log(e);
      setIsLoading(false);
      rememberRedirectUrl(`${location.pathname}${location.search}`);
      navigate("/");
    }
  };

  useEffect(() => {
    if (id && type && lang) {
      if (isPlatformApp === "1") {
        getReportDataForPlatformUser(id, type, lang);
      } else {
        getReportDataForUser(id, type, lang);
      }
    }
  }, [id, type, lang, isPlatformApp]);

  // Load category data for field formatting in ServicePage (run once on mount)
  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        await Promise.all([ASGetLogicCategoriesList(), ASGetBiomarkersList()]);
      } catch (err) {
        console.error("Error loading category data:", err);
      }
    };
    loadCategoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const svgConverter = useCallback(
    async (content: string, result: string, imgWidth: number) => {
      // Read both nodes synchronously at call time. If either is missing,
      // skip instead of letting html2canvas reject with
      // "Invalid element provided as first argument" (an unhandled rejection
      // that surfaces as the intermittent error overlay on first load).
      const element = document.getElementById(content);
      const resultContainer = document.getElementById(result);
      if (!element || !resultContainer) {
        console.warn(
          `svgConverter: skipping rasterization — source "${content}" present: ${!!element}, result "${result}" present: ${!!resultContainer}`
        );
        return;
      }

      try {
        const canvas = await html2canvas(element, {
          useCORS: true,
          backgroundColor: "transparent",
          allowTaint: true,
          scale: 1.5,
        });
        const img = new Image();
        img.src = canvas.toDataURL("image/jpg");
        img.width = imgWidth;
        resultContainer.appendChild(img);
      } catch (e) {
        console.warn(`svgConverter: html2canvas failed for "${content}"`, e);
      }
    },
    []
  );

  return (
    <React.Fragment>
      {isLoading && <PageLoader />}
      {/* Only white-label reports carry frontend translations; the default flow
          is English-only, so offering three languages there would promise a
          translation that does not exist. Outside #content, like GeneratePDF, so
          neither control is printed into the exported PDF. */}
      {data && isNativeApp !== "1" && getWhiteLabelTheme(data.customType) && (
        <ReportLangPicker
          value={uiLang}
          onChange={handleLangChange}
          label={getAyurmaStrings(uiLang).langPicker.label}
        />
      )}
      {data && isNativeApp !== "1" && (
        <GeneratePDF
          userName={
            data.PAGES?.find((page: any) => page.INDEX === 1)?.SUBJECT_NAME ??
            ""
          }
          type={type ?? ""}
          setIsCreatingPDF={setIsCreatingPDF}
          customType={data.customType}
          lang={uiLang}
          backendLang={lang}
        />
      )}
      {error && <ReportNotFound error={error} />}
      <div id="content" className={classes.content}>
        {data &&
          (type === "10" || type === "12" || type === "14") &&
          data.TRAINER_MAIL === "rick@millerhealth.london" && (
            <CustomReportAMR
              data={data}
              svgConverter={svgConverter}
              reportType={type}
            />
          )}
        {data &&
          (type === "10" || type === "12" || type === "14" || type === "18") &&
          data.TRAINER_MAIL !== "rick@millerhealth.london" && (
            <ReportAMR
              data={data}
              newData={newData}
              svgConverter={svgConverter}
              reportType={type}
              isCreatingPDF={isCreatingPDF}
              customerMail={data.TRAINER_MAIL}
            />
          )}
        {data &&
          (type === "11" || type === "13" || type === "15") &&
          data.TRAINER_MAIL === "rick@millerhealth.london" && (
            <CustomReportRMR
              data={data}
              svgConverter={svgConverter}
              reportType={type}
            />
          )}
        {data &&
          (type === "11" || type === "13" || type === "15" || type === "17") &&
          data.TRAINER_MAIL !== "rick@millerhealth.london" && (
            <ReportRMR
              data={data}
              newData={newData}
              svgConverter={svgConverter}
              reportType={type}
              isCreatingPDF={isCreatingPDF}
              lang={uiLang}
            />
          )}
        {data && type === "19" && (
          <ReportRMRCheatsheet
            data={data}
            svgConverter={svgConverter}
            reportType={type}
            isCreatingPDF={isCreatingPDF}
            reportId={id || undefined}
          />
        )}
        {/*{data && type === "16" && (*/}
        {/*  <StressReport*/}
        {/*    data={data}*/}
        {/*    svgConverter={svgConverter}*/}
        {/*    reportType={type}*/}
        {/*  />*/}
        {/*)}*/}
      </div>
    </React.Fragment>
  );
};

export default Report;
